'use client';

import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Upload, Download, Copy, Trash2, ArrowRight, ArrowDown, Code, Settings } from 'lucide-react';
import { saveAs } from 'file-saver';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { sqlMinifyContent } from '../../data/sql-minify-content';
import Link from 'next/link';

// Using sql-formatter library for SQL minification
import { format } from 'sql-formatter';

interface CodeEditorProps {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
}

const CodeEditor = ({ value, onChange, readOnly, placeholder, className }: CodeEditorProps) => {
    return (
        <div className={cn(
            "relative flex h-full w-full overflow-hidden rounded-md border bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            className
        )}>
            <Textarea
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                readOnly={readOnly}
                className="h-full w-full resize-none border-0 focus-visible:ring-0 rounded-none p-4 font-mono text-sm leading-6"
                placeholder={placeholder}
                spellCheck={false}
            />
        </div>
    );
};

type SqlDialect = 'sql' | 'mariadb' | 'mysql' | 'postgresql' | 'db2' | 'plsql' | 'n1ql' | 'redshift' | 'spark' | 'tsql';

interface MinifyOptions {
    language: SqlDialect;
    compress: boolean;
    preserveComments: boolean;
}

const defaultOptions: MinifyOptions = {
    language: 'sql',
    compress: true,
    preserveComments: false
};

const minifySql = (sql: string, options: MinifyOptions): { minified: string; error?: string } => {
    try {
        if (!sql.trim()) return { minified: '', error: 'Empty SQL content' };

        // First format with the sql-formatter to ensure consistent structure
        const formatted = format(sql, {
            language: options.language,
            tabWidth: 1,
            useTabs: false,
            keywordCase: 'lower',
            linesBetweenQueries: 0,
            denseOperators: true,
            expressionWidth: 1,
            indentStyle: 'standard'
        });

        // Then minify by removing unnecessary whitespace and newlines
        let minified = formatted
            .replace(/\/\*[\s\S]*?\*\//g, options.preserveComments ? ' $& ' : '') // Handle block comments
            .replace(/--.*$/gm, options.preserveComments ? ' $& ' : '') // Handle line comments
            .replace(/\s+/g, ' ') // Replace all whitespace sequences with a single space
            .replace(/\s*([=<>!+\-*\/%|&,;(){}[\]])\s*/g, '$1') // Remove spaces around operators and brackets
            .replace(/([^\w\s])\s+([^\w\s])/g, '$1$2') // Remove spaces between non-word characters
            .replace(/\s*([,;])\s*/g, '$1') // Remove spaces around commas and semicolons
            .replace(/\\(\s+)/g, ' ') // Remove extra spaces after backslashes
            .replace(/\s+\//g, '/') // Remove spaces before slashes
            .trim();

        // Additional compression if enabled
        if (options.compress) {
            minified = minified
                .replace(/([=<>!]=?|<>|>=?|<=?|\|\||&&|\+\+|--|\+=?|-=|\*=|\/=?|%=?|\^=|\|=?|\?\?=?) ?/g, '$1')
                .replace(/([^\w\s])\s+/g, '$1')
                .replace(/\s+([^\w\s])/g, '$1')
                .replace(/([^\w\s])\s+([^\w\s])/g, '$1$2')
                .replace(/([^\w\s])\s+([^\w\s])/g, '$1$2') // Run twice to catch overlapping matches
                .replace(/\s+/g, ' ')
                .trim();
        }

        return { minified };
    } catch (error) {
        console.error('Error minifying SQL:', error);
        return {
            minified: sql,
            error: error instanceof Error ? error.message : 'An error occurred while minifying SQL'
        };
    }
};

export default function SqlMinifier() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [options, setOptions] = useState<MinifyOptions>(defaultOptions);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [stats, setStats] = useState({
        originalSize: '0 B',
        minifiedSize: '0 B',
        saved: '0 B',
        percentage: 0
    });

    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const minifySqlHandler = () => {
        try {
            if (!input.trim()) {
                toast({
                    title: "Error",
                    description: "Please enter some SQL to minify",
                    variant: "destructive",
                });
                return;
            }

            const { minified, error } = minifySql(input, options);

            if (error) {
                throw new Error(error);
            }

            setOutput(minified);

            // Calculate and set stats
            const originalSize = new TextEncoder().encode(input).length;
            const minifiedSize = new TextEncoder().encode(minified).length;
            const saved = originalSize - minifiedSize;
            const percentage = Math.round((saved / originalSize) * 100);

            setStats({
                originalSize: formatFileSize(originalSize),
                minifiedSize: formatFileSize(minifiedSize),
                saved: formatFileSize(saved),
                percentage
            });

            toast({
                title: "Success",
                description: "SQL minified successfully!",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "An error occurred while minifying SQL",
                variant: "destructive",
            });
            console.error('Error minifying SQL:', error);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
            toast({
                title: "File Uploaded",
                description: `Loaded ${file.name}`,
            });
        };
        reader.readAsText(file);
    };

    const handleDownload = () => {
        if (!output) return;
        const blob = new Blob([output], { type: "text/plain;charset=utf-8" });
        saveAs(blob, "minified.sql");
        toast({
            title: "Downloaded",
            description: "File saved as minified.sql",
        });
    };

    const copyToClipboard = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        toast({
            title: "Copied!",
            description: "Minified SQL copied to clipboard",
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setStats({
            originalSize: '0 B',
            minifiedSize: '0 B',
            saved: '0 B',
            percentage: 0
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const sampleSql = `-- Sample SQL Query
SELECT 
    u.user_id,
    u.username,
    u.email,
    COUNT(o.order_id) AS total_orders,
    SUM(o.amount) AS total_spent
FROM 
    users u
LEFT JOIN 
    orders o ON u.user_id = o.user_id
WHERE 
    u.status = 'active'
    AND o.order_date >= '2023-01-01'
GROUP BY 
    u.user_id, u.username, u.email
HAVING 
    COUNT(o.order_id) > 5
ORDER BY 
    total_spent DESC
LIMIT 10;`;

    const loadSample = () => {
        setInput(sampleSql);
    };

    return (
        <div className="container mx-auto p-4 max-w-[1400px]">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <ToolPageIntro title="SQL Minifier">
                Minify SQL code to reduce file size and improve transmission speed. Our free SQL minifier removes whitespace and formatting while preserving functionality.
            </ToolPageIntro>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                        className="gap-2"
                    >
                        <Settings className="h-4 w-4" />
                        {isOptionsOpen ? 'Hide Options' : 'Minify Options'}
                    </Button>
                </div>
            </div>

            {/* Options Panel */}
            {isOptionsOpen && (
                <Card className="mb-6">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Minification Options</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">SQL Dialect</label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.language}
                                onChange={(e) => setOptions({ ...options, language: e.target.value as SqlDialect })}
                            >
                                <option value="sql">Standard SQL</option>
                                <option value="mariadb">MariaDB</option>
                                <option value="mysql">MySQL</option>
                                <option value="postgresql">PostgreSQL</option>
                                <option value="db2">DB2</option>
                                <option value="plsql">PL/SQL</option>
                                <option value="n1ql">N1QL</option>
                                <option value="redshift">Redshift</option>
                                <option value="spark">Spark SQL</option>
                                <option value="tsql">T-SQL</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none flex items-center">
                                <input
                                    type="checkbox"
                                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={options.preserveComments}
                                    onChange={(e) => setOptions({ ...options, preserveComments: e.target.checked })}
                                />
                                Preserve Comments
                            </label>
                            <p className="text-xs text-muted-foreground">
                                Keep comments in the minified output
                            </p>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none flex items-center">
                                <input
                                    type="checkbox"
                                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={options.compress}
                                    onChange={(e) => setOptions({ ...options, compress: e.target.checked })}
                                />
                                Aggressive Compression
                            </label>
                            <p className="text-xs text-muted-foreground">
                                More aggressive minification (may break some queries)
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Stats Bar */}
            {output && (
                <div className="mb-6 p-4 bg-muted/30 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                            <p className="text-sm text-muted-foreground">Original Size</p>
                            <p className="font-mono font-medium">{stats.originalSize}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Minified Size</p>
                            <p className="font-mono font-medium">{stats.minifiedSize}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Reduction</p>
                            <p className="font-mono font-medium">{stats.percentage}% ({stats.saved})</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Status</p>
                            <p className="font-medium text-green-600">Minified Successfully</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-250px)]">
                {/* Input Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-full">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <CardTitle className="flex justify-between items-center">
                            <span>Input SQL</span>
                            <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={loadSample} className="text-muted-foreground">
                                    Sample
                                </Button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept=".sql,.txt"
                                    className="hidden"
                                    id="file-upload"
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-muted-foreground"
                                >
                                    <Upload className="h-4 w-4 mr-1" /> Upload
                                </Button>
                                <Button variant="ghost" size="sm" onClick={clearAll} className="text-muted-foreground hover:text-destructive">
                                    <Trash2 className="h-4 w-4" /> Clear
                                </Button>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 relative overflow-hidden">
                        <CodeEditor
                            value={input}
                            onChange={setInput}
                            placeholder="Paste your SQL here, upload a file, or try the sample..."
                            className="border-0 rounded-none"
                        />
                    </CardContent>
                </Card>

                {/* Action Section */}
                <div className="flex lg:flex-col justify-center items-center gap-4 py-4 lg:py-0">
                    <Button
                        onClick={minifySqlHandler}
                        size="lg"
                        className="rounded-full w-16 h-16 lg:w-20 lg:h-20 p-0 shadow-lg hover:scale-105 transition-transform"
                        title="Minify SQL"
                    >
                        <ArrowRight className="h-8 w-8 hidden lg:block" />
                        <ArrowDown className="h-8 w-8 lg:hidden" />
                    </Button>
                </div>

                {/* Output Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-full">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <CardTitle className="text-lg font-medium">Minified Output</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={copyToClipboard} disabled={!output} title="Copy">
                                    <Copy className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon" onClick={handleDownload} disabled={!output} title="Download">
                                    <Download className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 bg-muted/30 relative overflow-hidden">
                        <CodeEditor
                            value={output}
                            readOnly
                            placeholder="Minified SQL will appear here..."
                            className="border-0 rounded-none bg-transparent"
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={sqlMinifyContent.title}
                description={sqlMinifyContent.description}
                content={sqlMinifyContent.content}
                faqs={sqlMinifyContent.faqs}
                relatedTools={sqlMinifyContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}
